import { useState, useEffect, useRef } from "react";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  getLandingPages,
  createLandingPage,
  updateLandingPage,
  deleteLandingPage,
} from "../api/LandingPagesApi";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Search, Globe, Trash2, ExternalLink, Copy, Eye, Edit, Code } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  url: "",
  submission_url: "",
  html_content: "",
  capture_credentials: true,
  redirect_url: "",
  page_type: "login",
};
const SEARCH_KEY = "search";

const LandingPages = () => {
  const [pages, setPages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const isFirstRender = useRef(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deletePage, setDeletePage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => fetchPages(search), search === "" ? 0 : 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPages = async (q = search) => {
    setLoading(true);
    try {
      const response = await getLandingPages(q ? { [SEARCH_KEY]: q } : {});
      setPages(response.data?.results || []);
      setSummary(response.data?.summary || null);
    } catch (error) {
      toast.error("Failed to load landing pages");
    }
    setLoading(false);
  };
  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setIsDialogOpen(true);
  };

  const openEdit = (page) => {
    setEditingId(page.id);
    setFormData({
      name: page.name || "",
      url: page.url || "",
      submission_url: page.submission_url || "",
      html_content: page.html_content || "",
      capture_credentials: page.capture_credentials !== false,
      redirect_url: page.redirect_url || "",
      page_type: page.page_type || "login",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await updateLandingPage(editingId, formData);
        toast.success("Landing page updated");
      } else {
        await createLandingPage(formData);
        toast.success("Landing page created");
      }
      setIsDialogOpen(false);
      setFormData(emptyForm);
      setEditingId(null);
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save landing page");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deletePage) return;

    setDeleting(true);

    try {
      await deleteLandingPage(deletePage.id);
      toast.success("Landing page deleted");
      setDeletePage(null);
      fetchPages();
    } catch (error) {
      toast.error("Failed to delete landing page");
    } finally {
      setDeleting(false);
    }
  };

  // Search ab backend se hota hai, isliye client-side filter nahi
  const filteredPages = Array.isArray(pages) ? pages : [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="landing-pages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Landing Pages</h1>
          <p className="text-gray-500 text-sm mt-1">Manage phishing landing pages for simulations</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2"
          data-testid="create-page-btn"
        >
          <Plus size={18} />
          New Landing Page
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-[#8c3add]/10">
              <Globe size={20} className="text-[#8c3add]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1f1f23]">{summary?.total_pages || 0}</p>
              <p className="text-xs text-gray-500">Total Pages</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-[#1fa7a1]/10">
              <Globe size={20} className="text-[#1fa7a1]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1f1f23]">{summary?.with_credential_capture || 0}</p>
              <p className="text-xs text-gray-500">With Credential Capture</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-100 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-[#f59e0b]/10">
              <ExternalLink size={20} className="text-[#f59e0b]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1f1f23]">{summary?.with_redirect || 0}</p>
              <p className="text-xs text-gray-500">With Redirect</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          placeholder="Search landing pages..."
          className="pl-10 pr-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="pages-search"
        />
      </div>

      {/* Pages Grid — TemplateLibrary jaisa: loader sirf is hisse me, header/filters hamesha visible */}
      {loading ? (
        <CulsightPageLoader />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pages-grid">
          {filteredPages.length === 0 ? (
            <div className="col-span-full py-12 text-center text-gray-500">No landing pages found</div>
          ) : (
            filteredPages.map((page) => (
            <Card
              key={page.id}
              className="border border-gray-100 shadow-sm card-hover"
              data-testid={`page-card-${page.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="p-2 rounded-lg bg-[#8c3add]/10">
                    <Globe size={20} className="text-[#8c3add]" />
                  </div>
                  <Badge className={page.capture_credentials ? "bg-[#1fa7a1]/10 text-[#1fa7a1]" : "bg-gray-100 text-gray-600"}>
                    {page.capture_credentials ? "Capturing" : "No Capture"}
                  </Badge>
                </div>

                <h3 className="font-semibold text-[#1f1f23] mt-3">{page.name}</h3>
                <p className="text-sm text-[#8c3add] mt-1 font-mono">{page.url}</p>

                {page.redirect_url && (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Redirects to:</p>
                    <p className="text-xs text-gray-700 truncate">{page.redirect_url}</p>
                  </div>
                )}
                {page.submission_url && (
                  <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs text-amber-700 font-medium">Form posts to:</p>
                    <p className="text-xs text-amber-900 truncate font-mono">{page.submission_url}</p>
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-400">{new Date(page.created_at).toLocaleDateString()}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 hover:bg-[#8c3add]/10"
                      onClick={() => setPreviewPage(page)}
                      data-testid={`view-page-${page.id}`}
                    >
                      <Eye size={16} className="text-[#8c3add]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 hover:bg-[#1fa7a1]/10"
                      onClick={() => openEdit(page)}
                      data-testid={`edit-page-${page.id}`}
                    >
                      <Edit size={16} className="text-[#1fa7a1]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 hover:bg-[#8c3add]/10"
                      onClick={() => {
                        navigator.clipboard.writeText(page.url);
                        toast.success("URL copied");
                      }}
                    >
                      <Copy size={16} className="text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 hover:bg-red-50"
                      onClick={() => setDeletePage(page)}
                      data-testid={`delete-page-${page.id}`}
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            ))
          )}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) { setEditingId(null); setFormData(emptyForm); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="page-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Edit size={18} className="text-[#8c3add]" /> : <Plus size={18} className="text-[#8c3add]" />}
              {editingId ? "Edit Landing Page" : "Create Landing Page"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Page Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Microsoft Login Clone"
                  required
                  data-testid="page-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Page Type</Label>
                <Select
                  value={formData.page_type}
                  onValueChange={(value) => setFormData({ ...formData, page_type: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="form">Generic Form</SelectItem>
                    <SelectItem value="download">Download / File</SelectItem>
                    <SelectItem value="error">Error / Awareness</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>URL Path</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="/login/microsoft"
                  required
                  data-testid="page-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Redirect URL (after capture)</Label>
                <Input
                  value={formData.redirect_url}
                  onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                  placeholder="https://microsoft.com"
                  data-testid="page-redirect-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Submission URL <span className="text-xs text-gray-400">(Optional — overrides form action for host-isolated capture)</span>
              </Label>
              <Input
                value={formData.submission_url}
                onChange={(e) => setFormData({ ...formData, submission_url: e.target.value })}
                placeholder="https://capture.acme.com/submit"
                data-testid="page-submission-input"
              />
              <p className="text-xs text-gray-500">
                Leave blank to post back to this same page. Set a different host when the landing page and capture endpoint must live on separate domains.
              </p>
            </div>

            <Tabs defaultValue="html" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="html" data-testid="html-tab">
                  <Code size={14} className="mr-1" /> HTML Source
                </TabsTrigger>
                <TabsTrigger value="preview" data-testid="preview-tab">
                  <Eye size={14} className="mr-1" /> Live Preview
                </TabsTrigger>
              </TabsList>
              <TabsContent value="html" className="space-y-2">
                <Label>HTML Content</Label>
                <Textarea
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  placeholder='<html><body><form method="POST">...</form></body></html>'
                  rows={14}
                  className="font-mono text-xs"
                  data-testid="page-html-input"
                />
                <p className="text-xs text-gray-500">
                  Forms posting to the page URL will be captured automatically when capture is enabled.
                </p>
              </TabsContent>
              <TabsContent value="preview">
                {formData.html_content ? (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <iframe
                      srcDoc={formData.html_content}
                      className="w-full h-96"
                      sandbox="allow-same-origin"
                      title="Landing Page Preview"
                    />
                  </div>
                ) : (
                  <div className="border rounded-lg p-12 text-center text-gray-400 text-sm">
                    Add HTML content to see live preview
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Capture Credentials</Label>
                <p className="text-xs text-gray-500 mt-0.5">Record any form submissions to this page</p>
              </div>
              <Switch
                checked={formData.capture_credentials}
                onCheckedChange={(checked) => setFormData({ ...formData, capture_credentials: checked })}
                data-testid="page-capture-switch"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#8c3add] hover:bg-[#702eb0]" disabled={saving} data-testid="page-submit-btn">
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create Page"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewPage} onOpenChange={() => setPreviewPage(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe size={20} className="text-[#8c3add]" />
              {previewPage?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500 text-xs">URL Path</Label>
                <p className="font-mono text-[#8c3add]">{previewPage?.url}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Page Type</Label>
                <p className="capitalize">{previewPage?.page_type || "login"}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500 text-xs">Capture Credentials</Label>
                <Badge className={previewPage?.capture_credentials ? "bg-[#1fa7a1]/10 text-[#1fa7a1]" : "bg-gray-100 text-gray-600"}>
                  {previewPage?.capture_credentials ? "Yes" : "No"}
                </Badge>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Redirect URL</Label>
                <p className="text-sm truncate">{previewPage?.redirect_url || "None"}</p>
              </div>
            </div>
            {previewPage?.html_content && (
              <div>
                <Label className="text-gray-500 text-xs">HTML Preview</Label>
                <div className="mt-2 border rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={previewPage.html_content}
                    className="w-full h-80"
                    sandbox="allow-same-origin"
                    title="Landing Page Preview"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewPage(null)}>Close</Button>
            {previewPage && (
              <Button
                className="bg-[#8c3add] hover:bg-[#702eb0] gap-2"
                onClick={() => { const p = previewPage; setPreviewPage(null); openEdit(p); }}
                data-testid="preview-edit-btn"
              >
                <Edit size={16} />
                Edit Page
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletePage}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeletePage(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete Landing Page
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this landing page?
            </p>

            {deletePage && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100">
                <p className="font-semibold text-gray-800">
                  {deletePage.name}
                </p>

                <p className="text-xs text-gray-500 mt-1 font-mono truncate">
                  {deletePage.url}
                </p>
              </div>
            )}

            <p className="text-xs text-red-500 mt-3">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeletePage(null)}
              disabled={deleting}
            >
              Cancel
            </Button>

            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPages;